import { useState, useRef, type FormEvent } from "react";
import { Link } from "react-router-dom";
import PageHero from "@/components/feature/PageHero";

const serviceOptions = [
  { label: "Fault or power issue", icon: "ri-flashlight-line" },
  { label: "Socket or switch repair", icon: "ri-plug-line" },
  { label: "Light fitting installation", icon: "ri-lightbulb-flash-line" },
  { label: "Consumer unit / fuse box", icon: "ri-dashboard-3-line" },
  { label: "Electrical safety check", icon: "ri-shield-check-line" },
  { label: "Landlord EICR certificate", icon: "ri-file-shield-2-line" },
  { label: "EV charger", icon: "ri-charging-pile-2-line" },
  { label: "Outdoor lighting", icon: "ri-sun-line" },
  { label: "Small commercial visit", icon: "ri-store-2-line" },
  { label: "Not sure yet", icon: "ri-question-line" },
];

const urgencyOptions = [
  { key: "flexible", label: "Flexible", hint: "Anytime soon", icon: "ri-time-line" },
  { key: "this-week", label: "This week", hint: "Within 5 days", icon: "ri-calendar-line" },
  { key: "asap", label: "As soon as possible", hint: "Today / tomorrow", icon: "ri-alarm-warning-line" },
];

const timeOptions = [
  { key: "morning", label: "Morning", hint: "8–12", icon: "ri-sun-foggy-line" },
  { key: "afternoon", label: "Afternoon", hint: "12–5", icon: "ri-sun-line" },
  { key: "evening", label: "Evening", hint: "5–7", icon: "ri-moon-line" },
];

const propertyTypes = [
  { label: "Flat", icon: "ri-building-2-line" },
  { label: "Terraced house", icon: "ri-home-4-line" },
  { label: "Semi-detached", icon: "ri-home-6-line" },
  { label: "Small shop", icon: "ri-store-2-line" },
  { label: "Small office", icon: "ri-building-line" },
  { label: "Café", icon: "ri-cup-line" },
  { label: "Other", icon: "ri-more-line" },
];

export default function BookPage() {
  const [step, setStep] = useState(1);
  const [service, setService] = useState<string>("");
  const [urgency, setUrgency] = useState<string>("this-week");
  const [propertyType, setPropertyType] = useState<string>("residential");
  const [propertyKind, setPropertyKind] = useState<string>("Flat");
  const [preferredTime, setPreferredTime] = useState<string>("");
  const [photoName, setPhotoName] = useState<string>("");
  const [descChars, setDescChars] = useState<number>(0);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [formError, setFormError] = useState<string>("");
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setFormError("");

    const form = e.currentTarget;
    const fd = new FormData(form);

    // Honeypot check
    const trap = String(fd.get("website_alt") || "").trim();
    if (trap) {
      // Silent success — bot behaviour
      setStatus("success");
      form.reset();
      return;
    }
    fd.delete("website_alt");

    // Remove empty file field to avoid multipart issues
    const photo = fd.get("photo");
    if (photo instanceof File && photo.size === 0) fd.delete("photo");

    // Convert to URL-encoded (photo not sent, just marked in a note)
    const payload = new URLSearchParams();
    fd.forEach((value, key) => {
      if (value instanceof File) {
        if (value.size > 0) payload.append(key, `Uncollectable: ${value.name}`);
      } else {
        payload.append(key, String(value));
      }
    });

    try {
      setSubmitting(true);
      const response = await fetch("https://readdy.ai/api/form/d96svg7pg5pqhofcuskg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = {};
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.meta?.detail || responseText || "";

      if (response.ok && parsed?.code === "OK") {
        setStatus("success");
        form.reset();
        setService("");
        setUrgency("this-week");
        setPropertyType("residential");
        setPropertyKind("Flat");
        setPreferredTime("");
        setPhotoName("");
        setDescChars(0);
        setStep(1);
      } else {
        setStatus("error");
        setFormError(
          serverMsg
            ? `Sorry — ${serverMsg}. Please try again or send a message from the Contact page.`
            : "Sorry — something went wrong sending your booking. Please try again."
        );
      }
    } catch {
      setStatus("error");
      setFormError("Sorry — network issue. Please try again in a moment.");
    } finally {
      setSubmitting(false);
    }
  }

  // default min date (today)
  const today = new Date().toISOString().split("T")[0];

  if (status === "success") {
    return (
      <section className="px-4 md:px-8 py-24 md:py-32 bg-background-50">
        <div className="max-w-2xl mx-auto text-center rounded-3xl bg-background-100 border border-background-200 p-8 md:p-14 fade-up">
          <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-accent-500 text-primary-950">
            <i className="ri-check-line text-3xl"></i>
          </div>
          <h1 className="mt-6 text-2xl sm:text-3xl md:text-5xl font-heading font-semibold tracking-tight text-foreground-950 leading-tight">
            Thanks — your appointment request has been received.
          </h1>
          <p className="mt-4 text-base md:text-lg text-foreground-600">
            You'll get confirmation shortly. Keep an eye on your email and messages.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-3.5 rounded-full bg-primary-950 text-background-50 text-sm font-semibold hover:bg-primary-900 transition-colors cursor-pointer whitespace-nowrap"
            >
              Back home
              <i className="ri-arrow-right-line"></i>
            </Link>
            <a
              href="https://wa.me/447700900123"
              className="inline-flex items-center gap-2 px-5 py-3.5 rounded-full border border-background-300 text-foreground-800 text-sm font-semibold hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-whatsapp-line"></i>
              Send a note on WhatsApp
            </a>
          </div>
        </div>
      </section>
    );
  }

  return (
    <>
      <PageHero
        eyebrow="Book online"
        title={
          <>
            Book a local<br />
            <em className="not-italic text-accent-400">electrician visit.</em>
          </>
        }
        intro="Takes about a minute. Add a photo if you can — it makes pricing faster."
        image="https://readdy.ai/api/search-image?query=Clean%20white%20electrician%20work%20van%20parked%20on%20a%20quiet%20Islington%20London%20terraced%20street%20with%20brick%20houses%2C%20warm%20golden%20morning%20light%2C%20realistic%20editorial%20photography%2C%20detailed%20texture&width=1400&height=900&seq=book-hero-01&orientation=landscape"
        imageAlt="Local electrician's van in Islington"
        primaryLabel="Start booking"
        primaryTo="#booking-form"
        secondaryLabel="View pricing"
        secondaryTo="/pricing"
      />

      <section id="booking-form" className="px-4 md:px-8 py-12 sm:py-16 md:py-24 bg-background-50">
        <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
          <aside className="lg:col-span-1 space-y-4 order-2 lg:order-1">
            <div className="p-4 sm:p-5 rounded-2xl bg-background-100 border border-background-200 lg:sticky lg:top-24">
              <p className="text-[11px] uppercase tracking-[0.18em] text-foreground-600">
                Booking progress
              </p>
              <div className="mt-4 flex lg:flex-col gap-3 lg:gap-3 overflow-x-auto scroll-clean">
                {["Choose service", "Pick a time", "Your details"].map((s, i) => {
                  const idx = i + 1;
                  const done = step > idx;
                  const active = step === idx;
                  return (
                    <div key={s} className="flex items-center gap-2 sm:gap-3 shrink-0">
                      <span
                        className={`w-8 h-8 flex items-center justify-center rounded-full text-xs font-semibold transition-colors shrink-0 ${
                          done
                            ? "bg-accent-500 text-primary-950"
                            : active
                              ? "bg-primary-950 text-background-50"
                              : "bg-background-50 text-foreground-500 border border-background-200"
                        }`}
                      >
                        {done ? <i className="ri-check-line"></i> : `0${idx}`}
                      </span>
                      <span
                        className={`text-sm font-medium whitespace-nowrap ${
                          active ? "text-foreground-950" : "text-foreground-600"
                        }`}
                      >
                        {s}
                      </span>
                    </div>
                  );
                })}
              </div>

              <div className="hidden lg:block mt-6 pt-6 border-t border-background-200 space-y-3">
                <p className="text-xs uppercase tracking-[0.18em] text-foreground-600">
                  Quick tips
                </p>
                <div className="flex items-start gap-2.5 text-xs text-foreground-700 leading-relaxed">
                  <i className="ri-camera-line text-accent-700 mt-0.5"></i>
                  <span>A photo of the fuse box or fault helps a lot.</span>
                </div>
                <div className="flex items-start gap-2.5 text-xs text-foreground-700 leading-relaxed">
                  <i className="ri-map-pin-line text-accent-700 mt-0.5"></i>
                  <span>Postcode is enough — no need to share the door number now.</span>
                </div>
                <div className="flex items-start gap-2.5 text-xs text-foreground-700 leading-relaxed">
                  <i className="ri-chat-3-line text-accent-700 mt-0.5"></i>
                  <span>I'll message you back within a few hours to confirm.</span>
                </div>
              </div>
            </div>

            <div className="p-4 sm:p-5 rounded-2xl bg-primary-950 text-background-100">
              <p className="text-xs uppercase tracking-[0.18em] text-accent-400 mb-2">
                Emergency?
              </p>
              <p className="text-sm leading-relaxed">
                If you can smell burning, see sparks, or have exposed live wires,
                turn off the power if safe to do so and call emergency services
                immediately.
              </p>
              <a
                href="tel:+447700900123"
                className="mt-4 inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-accent-500 text-primary-950 text-sm font-semibold hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-phone-fill"></i>
                07700 900123
              </a>
            </div>
          </aside>

          <form
            id="electrician-booking-form"
            data-readdy-form
            onSubmit={handleSubmit}
            className="lg:col-span-2 order-1 lg:order-2 rounded-2xl bg-background-50 border border-background-200 p-5 sm:p-6 md:p-10 space-y-8 sm:space-y-10"
          >
            {/* Honeypot */}
            <input
              type="text"
              name="website_alt"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
              className="hp-field"
            />

            {/* Step 1 — Service */}
            <fieldset>
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-2.5">
                  <span className="w-7 h-7 flex items-center justify-center rounded-full bg-primary-950 text-background-50 text-xs font-semibold">
                    01
                  </span>
                  <legend className="text-sm font-heading font-semibold text-foreground-950">
                    What do you need help with?
                  </legend>
                </div>
                <span className="text-[11px] uppercase tracking-wider text-foreground-500">
                  Tap one
                </span>
              </div>
              <p className="mt-2 mb-5 text-sm text-foreground-600">
                Not sure? Pick the closest — you can add detail below.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {serviceOptions.map((opt) => {
                  const active = service === opt.label;
                  return (
                    <label
                      key={opt.label}
                      className={`group flex items-center gap-3 p-3.5 rounded-xl border cursor-pointer transition-all ${
                        active
                          ? "border-primary-950 bg-primary-50 ring-2 ring-primary-200"
                          : "border-background-200 bg-background-50 hover:border-primary-300 hover:bg-background-100"
                      }`}
                    >
                      <input
                        type="radio"
                        name="service_type"
                        value={opt.label}
                        required
                        checked={active}
                        onChange={() => {
                          setService(opt.label);
                          setStep((s) => Math.max(s, 2));
                        }}
                        className="sr-only"
                      />
                      <span
                        className={`w-9 h-9 flex items-center justify-center rounded-lg text-lg transition-colors shrink-0 ${
                          active
                            ? "bg-accent-500 text-primary-950"
                            : "bg-background-100 text-foreground-700 group-hover:bg-background-200"
                        }`}
                      >
                        <i className={opt.icon}></i>
                      </span>
                      <span className="text-sm text-foreground-900 flex-1">{opt.label}</span>
                      {active && (
                        <i className="ri-check-line text-primary-950 text-lg"></i>
                      )}
                    </label>
                  );
                })}
              </div>
            </fieldset>

            {/* Step 2 — Timing */}
            <fieldset>
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-2.5">
                  <span className={`w-7 h-7 flex items-center justify-center rounded-full text-xs font-semibold transition-colors ${
                    step >= 2 ? "bg-primary-950 text-background-50" : "bg-background-100 text-foreground-500"
                  }`}>
                    02
                  </span>
                  <legend className="text-sm font-heading font-semibold text-foreground-950">
                    When works for you?
                  </legend>
                </div>
                <span className="text-[11px] uppercase tracking-wider text-foreground-500">
                  Urgency
                </span>
              </div>
              <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-2 mb-4">
                {urgencyOptions.map((u) => {
                  const active = urgency === u.key;
                  return (
                    <label
                      key={u.key}
                      className={`flex items-center gap-3 p-3.5 rounded-xl border cursor-pointer transition-all ${
                        active
                          ? "border-primary-950 bg-primary-50 ring-2 ring-primary-200"
                          : "border-background-200 bg-background-50 hover:border-background-300"
                      }`}
                    >
                      <input
                        type="radio"
                        name="urgency"
                        value={u.label}
                        required
                        checked={active}
                        onChange={() => {
                          setUrgency(u.key);
                          setStep((s) => Math.max(s, 3));
                        }}
                        className="sr-only"
                      />
                      <span
                        className={`w-10 h-10 flex items-center justify-center rounded-lg shrink-0 ${
                          active
                            ? "bg-accent-500 text-primary-950"
                            : "bg-background-100 text-foreground-700"
                        }`}
                      >
                        <i className={`${u.icon} text-lg`}></i>
                      </span>
                      <span className="flex flex-col">
                        <span className="text-sm font-medium text-foreground-950">{u.label}</span>
                        <span className="text-[11px] text-foreground-500">{u.hint}</span>
                      </span>
                    </label>
                  );
                })}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="flex flex-col gap-2">
                  <span className="text-xs uppercase tracking-[0.14em] text-foreground-600 flex items-center gap-1.5">
                    <i className="ri-calendar-line"></i>
                    Preferred date
                  </span>
                  <input
                    type="date"
                    name="preferred_date"
                    required
                    min={today}
                    className="px-4 py-3 rounded-xl bg-background-50 border border-background-200 text-sm text-foreground-950 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-all"
                  />
                </label>
                <div className="flex flex-col gap-2">
                  <span className="text-xs uppercase tracking-[0.14em] text-foreground-600 flex items-center gap-1.5">
                    <i className="ri-time-line"></i>
                    Preferred time
                  </span>
                  <div className="grid grid-cols-3 gap-2">
                    {timeOptions.map((t) => {
                      const active = preferredTime === t.key;
                      return (
                        <label
                          key={t.key}
                          className={`flex flex-col items-center justify-center gap-0.5 p-2.5 rounded-xl border cursor-pointer transition-all ${
                            active
                              ? "border-primary-950 bg-primary-950 text-background-50"
                              : "border-background-200 bg-background-50 text-foreground-800 hover:border-background-300"
                          }`}
                        >
                          <input
                            type="radio"
                            name="preferred_time"
                            value={`${t.label} (${t.hint})`}
                            required
                            checked={active}
                            onChange={() => setPreferredTime(t.key)}
                            className="sr-only"
                          />
                          <i className={`${t.icon} text-base`}></i>
                          <span className="text-xs font-medium leading-none">{t.label}</span>
                          <span className={`text-[10px] leading-none ${active ? "text-background-200" : "text-foreground-500"}`}>{t.hint}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              </div>
            </fieldset>

            {/* Step 3 — Your details */}
            <fieldset>
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-2.5">
                  <span className={`w-7 h-7 flex items-center justify-center rounded-full text-xs font-semibold transition-colors ${
                    step >= 3 ? "bg-primary-950 text-background-50" : "bg-background-100 text-foreground-500"
                  }`}>
                    03
                  </span>
                  <legend className="text-sm font-heading font-semibold text-foreground-950">
                    Where should I visit?
                  </legend>
                </div>
                <span className="text-[11px] uppercase tracking-wider text-foreground-500">
                  Almost done
                </span>
              </div>

              {/* Home / Business toggle */}
              <div className="mt-5 mb-5">
                <span className="text-xs uppercase tracking-[0.14em] text-foreground-600 mb-2 block">
                  This is a
                </span>
                <div className="grid grid-cols-2 gap-2 p-1 bg-background-100 rounded-xl">
                  {[
                    { k: "residential", label: "Home", icon: "ri-home-4-line" },
                    { k: "commercial", label: "Small business", icon: "ri-store-2-line" },
                  ].map((o) => {
                    const active = propertyType === o.k;
                    return (
                      <label
                        key={o.k}
                        className={`flex items-center justify-center gap-2 py-3 rounded-lg cursor-pointer text-sm font-medium transition-all ${
                          active
                            ? "bg-primary-950 text-background-50"
                            : "text-foreground-700 hover:bg-background-200"
                        }`}
                      >
                        <input
                          type="radio"
                          name="property_category"
                          value={o.label}
                          required
                          checked={active}
                          onChange={() => setPropertyType(o.k)}
                          className="sr-only"
                        />
                        <i className={`${o.icon} text-base`}></i>
                        {o.label}
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Property kind chips */}
              <div className="mb-5">
                <span className="text-xs uppercase tracking-[0.14em] text-foreground-600 mb-2 block">
                  Property type
                </span>
                <div className="flex flex-wrap gap-2">
                  {propertyTypes.map((p) => {
                    const active = propertyKind === p.label;
                    return (
                      <label
                        key={p.label}
                        className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-full border cursor-pointer text-sm transition-all ${
                          active
                            ? "border-primary-950 bg-primary-950 text-background-50"
                            : "border-background-200 bg-background-50 text-foreground-800 hover:border-background-300"
                        }`}
                      >
                        <input
                          type="radio"
                          name="property_type"
                          value={p.label}
                          required
                          checked={active}
                          onChange={() => setPropertyKind(p.label)}
                          className="sr-only"
                        />
                        <i className={`${p.icon} text-sm`}></i>
                        {p.label}
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Contact fields */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="flex flex-col gap-2">
                  <span className="text-xs uppercase tracking-[0.14em] text-foreground-600 flex items-center gap-1.5">
                    <i className="ri-user-line"></i> Name
                  </span>
                  <div className="relative">
                    <i className="ri-user-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400 pointer-events-none"></i>
                    <input
                      type="text"
                      name="name"
                      required
                      maxLength={80}
                      autoComplete="name"
                      placeholder="Your name"
                      className="w-full pl-10 pr-4 py-3 rounded-xl bg-background-50 border border-background-200 text-sm text-foreground-950 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-all"
                    />
                  </div>
                </label>
                <label className="flex flex-col gap-2">
                  <span className="text-xs uppercase tracking-[0.14em] text-foreground-600 flex items-center gap-1.5">
                    <i className="ri-phone-line"></i> Phone
                  </span>
                  <div className="relative">
                    <i className="ri-phone-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400 pointer-events-none"></i>
                    <input
                      type="tel"
                      name="phone"
                      required
                      maxLength={30}
                      autoComplete="tel"
                      placeholder="07…"
                      className="w-full pl-10 pr-4 py-3 rounded-xl bg-background-50 border border-background-200 text-sm text-foreground-950 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-all"
                    />
                  </div>
                </label>
                <label className="flex flex-col gap-2">
                  <span className="text-xs uppercase tracking-[0.14em] text-foreground-600 flex items-center gap-1.5">
                    <i className="ri-mail-line"></i> Email
                  </span>
                  <div className="relative">
                    <i className="ri-mail-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400 pointer-events-none"></i>
                    <input
                      type="email"
                      name="email"
                      required
                      maxLength={120}
                      autoComplete="email"
                      placeholder="you@example.co.uk"
                      className="w-full pl-10 pr-4 py-3 rounded-xl bg-background-50 border border-background-200 text-sm text-foreground-950 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-all"
                    />
                  </div>
                </label>
                <label className="flex flex-col gap-2">
                  <span className="text-xs uppercase tracking-[0.14em] text-foreground-600 flex items-center gap-1.5">
                    <i className="ri-map-pin-line"></i> Postcode
                  </span>
                  <div className="relative">
                    <i className="ri-map-pin-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400 pointer-events-none"></i>
                    <input
                      type="text"
                      name="postcode"
                      required
                      maxLength={12}
                      autoComplete="postal-code"
                      placeholder="N1 9AB"
                      className="w-full pl-10 pr-4 py-3 rounded-xl bg-background-50 border border-background-200 text-sm text-foreground-950 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-all uppercase"
                    />
                  </div>
                </label>
                <label className="flex flex-col gap-2 sm:col-span-2">
                  <span className="text-xs uppercase tracking-[0.14em] text-foreground-600 flex items-center gap-1.5">
                    <i className="ri-home-line"></i> Address
                    <span className="text-foreground-400 normal-case tracking-normal">— optional</span>
                  </span>
                  <div className="relative">
                    <i className="ri-home-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400 pointer-events-none"></i>
                    <input
                      type="text"
                      name="address"
                      maxLength={200}
                      autoComplete="street-address"
                      placeholder="Street and building"
                      className="w-full pl-10 pr-4 py-3 rounded-xl bg-background-50 border border-background-200 text-sm text-foreground-950 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-all"
                    />
                  </div>
                </label>
                <label className="flex flex-col gap-2 sm:col-span-2">
                  <span className="text-xs uppercase tracking-[0.14em] text-foreground-600 flex items-center justify-between">
                    <span className="flex items-center gap-1.5">
                      <i className="ri-file-text-line"></i> Job description
                    </span>
                    <span className={`normal-case tracking-normal text-[11px] ${descChars > 450 ? "text-accent-700 font-medium" : "text-foreground-500"}`}>
                      {descChars} / 500
                    </span>
                  </span>
                  <textarea
                    name="job_description"
                    required
                    maxLength={500}
                    rows={4}
                    onChange={(e) => setDescChars(e.target.value.length)}
                    placeholder="Tell me a bit about the job — access, what's not working, anything I should bring."
                    className="px-4 py-3 rounded-xl bg-background-50 border border-background-200 text-sm text-foreground-950 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-all resize-none"
                  ></textarea>
                </label>

                {/* Photo upload — nicer */}
                <div className="sm:col-span-2">
                  <span className="text-xs uppercase tracking-[0.14em] text-foreground-600 flex items-center gap-1.5 mb-2">
                    <i className="ri-camera-line"></i> Add a photo
                    <span className="text-foreground-400 normal-case tracking-normal">— optional but helpful</span>
                  </span>
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className={`w-full flex items-center gap-3 p-4 rounded-xl border-2 border-dashed text-left transition-all cursor-pointer ${
                      photoName
                        ? "border-accent-500 bg-accent-100/40"
                        : "border-background-300 bg-background-100 hover:border-primary-400 hover:bg-background-50"
                    }`}
                  >
                    <span className={`w-11 h-11 flex items-center justify-center rounded-lg shrink-0 ${
                      photoName ? "bg-accent-500 text-primary-950" : "bg-background-50 text-foreground-700"
                    }`}>
                      <i className={photoName ? "ri-check-line text-xl" : "ri-image-add-line text-xl"}></i>
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm font-medium text-foreground-950 truncate">
                        {photoName || "Tap to add a photo"}
                      </span>
                      <span className="block text-xs text-foreground-600 mt-0.5">
                        {photoName ? "Photo attached — tap to change" : "JPG or PNG · from camera or gallery"}
                      </span>
                    </span>
                    {photoName && (
                      <span
                        role="button"
                        tabIndex={0}
                        aria-label="Remove photo"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (fileInputRef.current) fileInputRef.current.value = "";
                          setPhotoName("");
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            e.stopPropagation();
                            if (fileInputRef.current) fileInputRef.current.value = "";
                            setPhotoName("");
                          }
                        }}
                        className="w-8 h-8 flex items-center justify-center rounded-full text-foreground-600 hover:bg-background-200 cursor-pointer"
                      >
                        <i className="ri-close-line"></i>
                      </span>
                    )}
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    name="photo"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      setPhotoName(f ? f.name : "");
                    }}
                  />
                </div>
              </div>

              <label className="mt-6 flex items-start gap-3 p-4 rounded-xl bg-background-100 border border-background-200 cursor-pointer">
                <input
                  type="checkbox"
                  name="consent"
                  required
                  value="agreed"
                  className="mt-0.5 w-4 h-4 rounded border-background-300 accent-primary-950 cursor-pointer shrink-0"
                />
                <span className="text-xs text-foreground-700 leading-relaxed">
                  I'm happy for Watts &amp; Wire to contact me about this booking.
                  My details won't be used for marketing.
                </span>
              </label>
            </fieldset>

            {status === "error" && formError && (
              <div className="flex items-start gap-3 p-4 rounded-xl bg-red-50 border border-red-200 text-sm text-red-800">
                <i className="ri-error-warning-line text-lg mt-0.5"></i>
                <p>{formError}</p>
              </div>
            )}

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 pt-2">
              <p className="text-xs text-foreground-500 max-w-sm">
                <i className="ri-shield-check-line mr-1 text-accent-700"></i>
                You'll get a confirmation once your slot is checked. Usually within a few hours.
              </p>
              <button
                type="submit"
                disabled={submitting}
                className="group inline-flex items-center justify-center gap-2 px-5 sm:px-6 py-3.5 sm:py-4 rounded-full bg-accent-500 text-primary-950 text-sm font-semibold hover:bg-accent-600 transition-all hover:-translate-y-0.5 cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i> Sending…
                  </>
                ) : (
                  <>
                    Book My Appointment
                    <i className="ri-arrow-right-up-line transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"></i>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </section>
    </>
  );
}